import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Icon, Grid, Menu, Popup, Transition } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import { connect } from 'react-redux'
import supplierActions from '../../store/actions/suppliers'
import icons from '../Icons'
import './Sidebar.scss'
import styles from './../../styles/global.module.scss'
import { useUniversalLateralMenuHook } from 'hooks/useUniversalLateralMenuHook'
import { isTabSwitchNavigation, useTabNavigation } from 'hooks/useTabNavigation'

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window
    return { width, height }
}

const isImageIcon = (v) =>
    typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('data:'))

const normalizeGroup = (s = '') =>
    (s ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

const stripQueryHash = (s = '') => String(s).split('#')[0].split('?')[0]

const normalizePath = (s = '/') => {
    const clean = stripQueryHash(s || '/').toLowerCase().trim()
    const withSlash = ('/' + clean).replace(/\/+/g, '/')
    return withSlash !== '/' ? withSlash.replace(/\/$/, '') : '/'
}

const getRawPath = (loc) => {
    if (typeof loc?.hash === 'string' && loc.hash.startsWith('#')) {
        return loc.hash.slice(1)
    }
    return loc?.pathname || '/'
}

const stripBasePrefix = (pathname) => {
    const norm = normalizePath(pathname)
    const parts = norm.split('/')
    const baseSet = new Set(['dashboard', 'home', 'inicio'])
    return (parts.length > 2 && baseSet.has(parts[1])) ? '/' + parts.slice(2).join('/') : norm
}

const equalsOrParentOf = (current, target) =>
    current === target || current.startsWith(target + '/')

const SupplierSidebarMenu = (props) => {
    const { t } = useTranslation()
    const { expand, setExpand, supplier, location, mobileOnly, setMobileOnly, lang, onMenuOpen, onTabOpen } = props

    // lateralMenu ahora viene del hook con persistencia en backend
    const { lateralMenu } = useUniversalLateralMenuHook('supplier');

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())
    const [availableRoutes, setAvailableRoutes] = useState([])
    const [allowedRoutes, setAllowedRoutes] = useState([])
    const [openHeaderIndices, setOpenHeaderIndices] = useState([])
    const [openSubHeaderTitles, setOpenSubHeaderTitles] = useState([])
    const [companyLogoSrc, setCompanyLogoSrc] = useState(null)
    const [stickyOffset, setStickyOffset] = useState(150)

    const [previousExpand, setPreviousExpand] = useState(null)

    const activeItemRef = useRef(null)
    const containerRef = useRef(null)
    const companyLogoFrameRef = useRef(null)
    const manualToggleForPath = useRef(null)
    const initialCollapseApplied = useRef(false) // Bandera para aplicar colapso solo una vez
    const closeStickyRef = useRef(null)

    const underlineMenu = false


    // Sincronizar lateralMenu con expand en TIEMPO REAL
    useEffect(() => {
        if (mobileOnly || lateralMenu === null) return;
        if (lateralMenu === true) {
            setExpand(1);
        } else if (lateralMenu === false) {
            if (typeof onTabOpen !== 'function') setExpand(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lateralMenu]);

const groupIconMap = useMemo(() => {
        const map = new Map()
            ; (icons || []).forEach((i) => map.set(normalizeGroup(i.title), i.url))
        return map
    }, [])
    
    const { tabs: openTabs, activeTabId } = useTabNavigation();

    // Fetch modules + permissions
    useEffect(() => {
        componentDidMount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const componentDidMount = async () => {
        try {
            const supplierId = supplier?.id
            const promises = [props.getSupplierModules()]
            promises.push(supplierId ? props.getWebSupplierPermissions(supplierId) : Promise.resolve([]))
            const [av, allowed] = await Promise.all(promises)
            setAvailableRoutes(av || [])
            setAllowedRoutes(allowed || [])
        } catch (err) {
            console.error('SupplierSidebarMenu load error', err)
            setAvailableRoutes([])
            setAllowedRoutes([])
        }
    }

    // (Pin lateral persistence ahora manejado por useUniversalLateralMenuHook)

    // Resize con debounce
    useEffect(() => {
        let timeout
        function handleResize() {
            clearTimeout(timeout)
            timeout = setTimeout(() => {
                setWindowDimensions(getWindowDimensions())
            }, 150)
        }
        window.addEventListener('resize', handleResize)
        return () => {
            clearTimeout(timeout)
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    // Mobile behavior
    useEffect(() => {
        if (windowDimensions.width <= 425) {
            setMobileOnly(true)
            if (expand === 1 || expand == null) setExpand(1)
        } else {
            setMobileOnly(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions])
    // mantener sidebar abierto al navegar; en móvil asegurar visible
    useEffect(() => {
        if (!location) return
        if (mobileOnly) setExpand(1)
    }, [location, mobileOnly])

    const getLogoFitScore = (naturalWidth, naturalHeight, frameWidth, frameHeight) => {
        if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return -1

        const containScale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight)
        const renderedWidth = naturalWidth * containScale
        const renderedHeight = naturalHeight * containScale
        const widthRatio = renderedWidth / frameWidth
        const heightRatio = renderedHeight / frameHeight
        const usedRatio = Math.max(widthRatio, heightRatio)

        const verticalPenalty = heightRatio > 0.94 ? (heightRatio - 0.94) * 0.15 : 0
        const compactPenalty = widthRatio < 0.56 ? (0.56 - widthRatio) * 0.35 : 0
        return usedRatio - verticalPenalty - compactPenalty
    }

    useEffect(() => {
        const menuLogo = supplier?.sys_company_data?.menu_logo_url || null
        const defaultLogo = supplier?.sys_company_data?.logo_url || null
        const candidates = [menuLogo, defaultLogo].filter(Boolean)

        if (!candidates.length) {
            setCompanyLogoSrc(null)
            return
        }

        if (candidates.length === 1) {
            setCompanyLogoSrc(candidates[0])
            return
        }

        const frameEl = companyLogoFrameRef.current
        const frameWidth = frameEl?.clientWidth || 0
        const frameHeight = frameEl?.clientHeight || 0

        if (!frameWidth || !frameHeight) {
            setCompanyLogoSrc(menuLogo || defaultLogo)
            return
        }

        let cancelled = false

        const loadDimensions = (src) => {
            return new Promise((resolve) => {
                const image = new Image()
                image.onload = () => {
                    resolve({
                        src,
                        naturalWidth: image.naturalWidth || 0,
                        naturalHeight: image.naturalHeight || 0,
                    })
                }
                image.onerror = () => resolve(null)
                image.src = src
            })
        }

        Promise.all(candidates.map(loadDimensions)).then((results) => {
            if (cancelled) return

            const ranked = (results || [])
                .filter(Boolean)
                .map((item) => ({
                    ...item,
                    score: getLogoFitScore(
                        item.naturalWidth,
                        item.naturalHeight,
                        frameWidth,
                        frameHeight
                    ),
                }))
                .sort((a, b) => b.score - a.score)

            setCompanyLogoSrc(ranked[0]?.src || menuLogo || defaultLogo)
        })

        return () => {
            cancelled = true
        }
    }, [
        supplier?.sys_company_data?.menu_logo_url,
        supplier?.sys_company_data?.logo_url,
        windowDimensions.width,
        expand,
    ])

    // Calcular offset del sticky header para el contenido del menú
    useEffect(() => {
        const updateStickyOffset = () => {
            if (!closeStickyRef.current) return
            const nextOffset = closeStickyRef.current.offsetHeight || 0
            if (nextOffset > 0) setStickyOffset(nextOffset)
        }

        const id = window.requestAnimationFrame(updateStickyOffset)
        window.addEventListener('resize', updateStickyOffset)
        return () => {
            window.cancelAnimationFrame(id)
            window.removeEventListener('resize', updateStickyOffset)
        }
    }, [expand, companyLogoSrc, windowDimensions.width])

    // Construir headers/routes a partir de availableRoutes + allowedRoutes
    const modulesToRender = useMemo(() => {
        if (!availableRoutes || !availableRoutes.length) return []
        return availableRoutes
            .filter((module) => {
                const moduleName = Object.keys(module)[0]
                const submodules = module[moduleName].routes || []
                return submodules.some((route) => allowedRoutes.includes(route.url))
            })
            .map((module) => {
                const moduleName = Object.keys(module)[0]
                const data = module[moduleName] || {}
                return {
                    module_group: moduleName,
                    module_group_en: data.name_en,
                    module_group_pt: data.name_pt,
                    module_group_ar: data.name_ar,
                    icon: data.icon,
                    routes: (data.routes || []).filter((r) => allowedRoutes.includes(r.url)),
                }
            })
    }, [availableRoutes, allowedRoutes])

    const headers = useMemo(() => {
        return modulesToRender.reduce((accu, module) => {
            const x = accu.find((item) => item.module_group === module.module_group);
            return x ? accu : accu.concat([module]);
        }, []);
    }, [modulesToRender])

    const subHeaders = useMemo(() => {
        const arr = modulesToRender.reduce((accu, module) => {
            (module.routes || []).forEach(r => {
                if (r.sub_group && !accu.find(item => item.sub_group === r.sub_group)) {
                    accu.push({
                        title: r.sub_group,
                        name_en: r.name_en,
                        name_pt: r.name_pt,
                        name_ar: r.name_ar,
                        group: module.module_group,
                        sub_group: r.sub_group,
                        ...r,
                    })
                }
            })
            return accu
        }, [])
        return _.uniqBy(arr, 'sub_group')
    }, [modulesToRender])

const routes = useMemo(() => {
        return modulesToRender.flatMap((m) =>
            (m.routes || []).map((r) => ({ ...r, group: m.module_group }))
        )
    }, [modulesToRender])

    // Ruta actual
    const rawPath = getRawPath(location)
    const currentPath = normalizePath(rawPath)
    const appPath = stripBasePrefix(rawPath)
    const shouldPreserveSidebarState = isTabSwitchNavigation(location)

const isActiveRoute = useCallback((routeUrl) => {
        if (!routeUrl) return false
        const target = normalizePath(`/${routeUrl}`)
        return equalsOrParentOf(appPath, target) || equalsOrParentOf(currentPath, target)
    }, [currentPath, appPath])

    const normalizeTabPath = (path) => {
        const normalized = `/${path || ''}`.replace(/\/+/g, '/')
        return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
    }
    const isRouteInOpenTab = (routePath) => {
        if (!openTabs || !Array.isArray(openTabs)) return false
        const normalized = normalizeTabPath(routePath)
        return openTabs.some(tab => normalizeTabPath(tab.path) === normalized)
    }
    const isRouteInActiveTab = (routePath) => {
        if (!openTabs || !activeTabId) return false
        const normalized = normalizeTabPath(routePath)
        const activeTab = openTabs.find(tab => tab.id === activeTabId)
        return activeTab && normalizeTabPath(activeTab.path) === normalized
    }

    const activeRoute = routes.find(r => isActiveRoute(r.url))
    const activeGroup = activeRoute?.group || null

    // Forzar sidebar contraído al entrar al dashboard (caso post-login) - SOLO UNA VEZ
    useEffect(() => {
        if (shouldPreserveSidebarState) return
        // Solo ejecutar si aún no se ha aplicado el colapso inicial
        if (initialCollapseApplied.current) return

        // Detectar entrada al "inicio" de la app (ruta que suele abrirse después del login)
        const isEntryPoint =
            appPath === '/' ||
            currentPath === '/' ||
            rawPath === '/supplier/supplier_dashboard' ||
            rawPath === '/supplier' ||
            rawPath === '/dashboard' ||
            rawPath === '/home' ||
            rawPath === '/inicio'

        if (isEntryPoint) {
            // cerrar cualquier header/subheader abierto y evitar auto-open inmediato
            setOpenHeaderIndices([])
            setOpenSubHeaderTitles([])
            manualToggleForPath.current = currentPath
            // Marcar que ya se aplicó el colapso inicial (no volver a ejecutar)
            initialCollapseApplied.current = true
        }
        // Ejecutar solo al montar y cuando cambia la ruta (pero la bandera evita re-ejecución)
    }, [location?.pathname, location?.hash, shouldPreserveSidebarState])

    // Auto-open módulo activo
    useEffect(() => {
        if (!lateralMenu) return
        if (shouldPreserveSidebarState) return
        if (!routes?.length || !headers?.length) return
        if (manualToggleForPath.current === currentPath) {
            manualToggleForPath.current = null
            return
        }
        const activeRoute = routes.find(r => isActiveRoute(r.url))
        if (!activeRoute) return
        const hIndex = headers.findIndex(h => h.module_group === activeRoute.group)
        if (hIndex >= 0) {
            setOpenHeaderIndices(prev => (prev.includes(hIndex) ? prev : [...prev, hIndex]))
        }
        if (activeRoute.sub_group) {
            setOpenSubHeaderTitles(prev => (prev.includes(activeRoute.sub_group) ? prev : [...prev, activeRoute.sub_group]))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath, routes, headers, lateralMenu, isActiveRoute, shouldPreserveSidebarState])

    // Scroll al item activo
    useEffect(() => {
        if (shouldPreserveSidebarState) return
        const t = setTimeout(() => {
            const el = activeItemRef.current
            const container = containerRef.current
            if (!el || !container) return
            try {
                const elRect = el.getBoundingClientRect()
                const contRect = container.getBoundingClientRect()
                if (elRect.top < contRect.top || elRect.bottom > contRect.bottom) {
                    const offset = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2
                    container.scrollTo({ top: Math.max(0, offset), behavior: 'auto' })
                }
            } catch (e) {
                try { el.scrollIntoView({ block: 'nearest', behavior: 'auto' }) } catch { el.scrollIntoView() }
            }
        }, 50)
        return () => clearTimeout(t)
    }, [currentPath, expand, shouldPreserveSidebarState])

    // Handlers
    const handleHeaderClick = useCallback((e, titleObj) => {
        e.preventDefault()
        e.stopPropagation()
        const index = titleObj.index ?? titleObj
        manualToggleForPath.current = currentPath
        setOpenHeaderIndices((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        )
    }, [currentPath])

    const handleSubHeaderClick = useCallback((e, titleObj) => {
        e.preventDefault()
        e.stopPropagation()
        const key = titleObj?.title ?? titleObj
        manualToggleForPath.current = currentPath
        setOpenSubHeaderTitles((prev) => (prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]))
    }, [currentPath])

    // helper para recolectar rutas según header o subheader
    const collectRoutesFor = (opts = {}) => {
        if (!Array.isArray(routes)) return []
        if (opts.subTitle) {
            const targetSub = normalizeGroup(opts.subTitle)
            return routes
                .filter(r => normalizeGroup(r.sub_group) === targetSub)
                .map(r => ({ ...r, icon: r.icon || null, children: (r.children || []).map(c => ({ ...c, icon: c.icon || null })) }))
        }
        if (opts.headerKey) {
            const targetGroup = normalizeGroup(opts.headerKey)
            return routes
                .filter(r => normalizeGroup(r.group) === targetGroup)
                .map(r => ({ ...r, icon: r.icon || null, children: (r.children || []).map(c => ({ ...c, icon: c.icon || null })) }))
        }
        return []
    }

    // Decorar handlers para emitir al BentoGrid cuando se abren/ciernan
    const handleHeaderClickDecorated = useCallback((e, titleObj) => {
        e.preventDefault()
        e.stopPropagation()
        const index = titleObj.index ?? titleObj
        manualToggleForPath.current = currentPath
        const isOpening = !openHeaderIndices.includes(index)
        setOpenHeaderIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]))

        const header = headers[index]
        if (!header) return

        if (typeof onMenuOpen === 'function') {
            const headerKey = header.module_group
            if (isOpening) {
                const routesForHeader = collectRoutesFor({ headerKey })
                console.debug && console.debug('[SupplierSidebarMenu] onMenuOpen header', { headerKey, routesForHeader })
                onMenuOpen({ type: 'header', key: normalizeGroup(headerKey), header, routes: routesForHeader })
            } else {
                console.debug && console.debug('[SupplierSidebarMenu] onMenuOpen clear (header closed)')
                onMenuOpen(null)
            }
        }
    }, [currentPath, headers, openHeaderIndices, onMenuOpen, routes])

    const handleSubHeaderClickDecorated = useCallback((e, titleObj) => {
        e.preventDefault()
        e.stopPropagation()
        const key = titleObj?.title ?? titleObj
        manualToggleForPath.current = currentPath
        const isOpening = !openSubHeaderTitles.includes(key)
        setOpenSubHeaderTitles((prev) => (prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]))

        if (typeof onMenuOpen === 'function') {
            if (isOpening) {
                const routesForSub = collectRoutesFor({ subTitle: key })
                console.debug && console.debug('[SupplierSidebarMenu] onMenuOpen subheader', { subTitle: key, routesForSub })
                onMenuOpen({ type: 'subheader', key: normalizeGroup(key), subTitle: key, routes: routesForSub })
            } else {
                const parentGroup = routes.find(r => normalizeGroup(r.sub_group) === normalizeGroup(key))?.group
                if (parentGroup) {
                    const routesForHeader = collectRoutesFor({ headerKey: parentGroup })
                    console.debug && console.debug('[SupplierSidebarMenu] onMenuOpen restoring header', { parentGroup, routesForHeader })
                    onMenuOpen({ type: 'header', key: normalizeGroup(parentGroup), header: headers.find(h => h.module_group === parentGroup), routes: routesForHeader })
                } else {
                    console.debug && console.debug('[SupplierSidebarMenu] onMenuOpen clear (no parent)')
                    onMenuOpen(null)
                }
            }
        }
    }, [currentPath, openSubHeaderTitles, onMenuOpen, headers, routes])

    const handleCloseMenuClick = useCallback(() => {
        // Si el pin está fijado y no estamos en móvil, no hacer nada
        if (lateralMenu && !mobileOnly) return

        setExpand((prev) => {
            setPreviousExpand(prev)
            if (!mobileOnly) return prev === 1 ? (previousExpand == null || previousExpand === 2) ? 0 : 2 : 1
            return !prev ? 1 : 0
        })
    }, [lateralMenu, mobileOnly, previousExpand, setExpand])

    // (Pin ya no se usa, el fijado se maneja desde ModalMenu)

    // Helpers de profundidad
    const getHeaderDepthClass = (headerIndex) =>
        openHeaderIndices.includes(headerIndex) ? 'depth-parent' : ''

    const getSubHeaderDepthClass = (subHeaderTitle) =>
        openSubHeaderTitles.includes(subHeaderTitle) ? 'depth-parent' : ''

    const getRouteDepthClassForSubHeader = (subHeaderTitle) =>
        openSubHeaderTitles.includes(subHeaderTitle) ? 'depth-current' : ''

    const getRouteDepthClassForHeader = (headerIndex) =>
        openHeaderIndices.includes(headerIndex) ? 'depth-current' : ''

    const getHeaderIconValue = (header) => {
        const key = normalizeGroup(header.module_group)
        return groupIconMap.get(key) ?? header.icon ?? 'archive'
    }

    const handleClickLink = (route) => {
        props.actionClick(route)
    }

    const handleTabOpen = (e, route) => {
        if (typeof onTabOpen === 'function') {
            e.preventDefault()
            handleClickLink(route)
            onTabOpen(route)
        }
    }

    return (
        <div
            className='sidebar'
            ref={containerRef}
            style={{
                width: expand === null || expand === 1 ? '225px' : '0px',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {/* Logo + botón hamburguesa (mismo layout que SidebarMenu de usuario) */}
            {(expand === null || expand === 1) && (
                <div className='Sidebar__CloseStickyy' ref={closeStickyRef} style={{ padding: 0, margin: 0 }}>
                    <div className='Sidebar__Header'>
{/* Fila 1: Solo botón hamburguesa */}
                        <div className='Sidebar__MenuButtonRow' style={{justifyContent:'flex-start',paddingLeft:'0',paddingRight:'0'}}>
                            <button
                                className='Sidebar__MenuButton Sidebar__MenuButton--shell'
                                onClick={() => {
                                    if (lateralMenu && !mobileOnly) {
                                        return
                                    }

                                    setExpand((previousExpandedState) => {
                                        setPreviousExpand(previousExpandedState)
                                        const newExpandState = !mobileOnly
                                            ? previousExpandedState == 1
                                                ? (previousExpand == null || previousExpand == 2) ? 0 : 2
                                                : 1
                                            : !previousExpandedState ? 1 : 0

                                        return newExpandState
                                    })
                                }}
                                aria-label={t('Abrir/cerrar menú lateral')}
                                style={{background:'transparent',border:'1.1px solid #6c6c6c',borderRadius:7,padding:'0 13px 0 3px',height:26,minWidth:62,display:'flex',alignItems:'center',gap:3,boxShadow:'none',justifyContent:'flex-start',textAlign:'left'}}
                            >
                                <img
                                    src='https://img.icons8.com/?size=100&id=100033&format=png&color=000000'
                                    alt='Menu'
                                    style={{width:14.5,height:14.5,marginRight:-6,marginLeft:0,display:'inline-block'}}
                                />
                                <span style={{fontSize:13,color:'#6c6c6c',fontWeight:500,whiteSpace:'nowrap',marginLeft:0}}>ocultar</span>
                            </button>
                        </div>

                        {/* Fila 2: Logo expandido */}
                        <div className='Sidebar__LogoRow'>
                            <div className='Sidebar__LogoSlot'>
                                <div className='Sidebar__LogoFrame' ref={companyLogoFrameRef}>
                                    {companyLogoSrc ? (
                                        <img
                                            src={companyLogoSrc}
                                            alt={t('Company Logo')}
                                            className='Sidebar__CompanyLogo SideBar__Outline'
                                            onError={() => {
                                                setCompanyLogoSrc(supplier?.sys_company_data?.logo_url || null)
                                            }}
                                            onClick={e => e.stopPropagation()}
                                            onMouseDown={e => e.stopPropagation()}
                                        />
                                    ) : (
                                        <div className='Sidebar__LogoPlaceholder' />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Transition visible={expand === null || expand === 1} animation='fade down' duration={220} unmountOnHide={false}>
                <div style={{ marginTop: stickyOffset }}>
                    {headers.map((header, index) => {
                        const isOpen = openHeaderIndices.includes(index);
                        return (
                            <Accordion key={`accordion-${header.module_group}-${index}`} fluid style={{ paddingLeft: 4, paddingBottom: 5 }}>
                                <Accordion.Title
                                    active={isOpen}
                                    index={index}
                                    onClick={(e) => handleHeaderClickDecorated(e, { index })}
                                    className={getHeaderDepthClass(index)}
                                >
<Menu.Item
                                        className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}${activeGroup === header.module_group ? ' is-active' : ''}`}
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', paddingLeft: 6 }}
                                    >
                                        <Icon
                                            name='dropdown'
                                            style={{
                                                marginRight: 6,
                                                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                opacity: isOpen ? 1 : 0,
                                                transition: 'opacity 220ms cubic-bezier(.2,.8,.2,1), transform 220ms cubic-bezier(.2,.8,.2,1)',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        {(() => {
                                            const iconValue = getHeaderIconValue(header)
                                            return isImageIcon(iconValue) ? (
                                                <img
                                                    src={iconValue}
                                                    alt={header.module_group}
                                                    style={{ width: 24, height: 24, verticalAlign: 'middle', marginRight: 8 }}
                                                    className='SideBar__Outline'
                                                />
                                            ) : (
                                                <Icon name={iconValue} size='large' className='SideBar__Outline' style={{ marginRight: 8 }} />
                                            )
                                        })()}
                                        {(expand === null || expand === 1) && (
                                            <h5 className={`menu-row-title ${getHeaderDepthClass(index)}`} style={{ margin: 0, marginLeft: 6 }}>
                                                {t(header.module_group)}
                                            </h5>
                                        )}
                                    </Menu.Item>
                                </Accordion.Title>

                                <Transition visible={isOpen} animation='fade down' duration={220} unmountOnHide={false}>
                                    <Accordion.Content active={isOpen} style={{ marginTop: 0, paddingTop: 0 }}>
                                        <Grid divided='vertically' style={{ width: '100%' }}>
                                            <Grid.Row style={{ paddingBottom: 0 }} />
                                            <Grid.Row style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 15 }}>
                                                <Grid.Column>
                                                    {/* SubHeaders */}
                                                    {subHeaders.map((subHeader, indexSH) => {
                                                        if (subHeader.group !== header.module_group) return null
                                                        const isSubOpen = openSubHeaderTitles.includes(subHeader.title)
                                                        return (
                                                            <Accordion
                                                                key={`sub-accordion-${subHeader.title}-${indexSH}`}
                                                                fluid
                                                                style={{ marginTop: 0, paddingTop: 0 }}
                                                            >
                                                                <Accordion.Title
                                                                    active={isSubOpen}
                                                                    onClick={(e) => handleSubHeaderClickDecorated(e, { title: subHeader.title })}
                                                                    style={{ marginBottom: 0, paddingBottom: 0 }}
                                                                    className={`${getSubHeaderDepthClass(subHeader.title)} ${routes.some(r => r.sub_group === subHeader.title && isActiveRoute(r.url)) ? 'has-active' : ''}`}
                                                                >
                                                                    <Menu.Item className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}`} style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                                                                        <Icon
                                                                            name='dropdown'
                                                                            style={{
                                                                                marginRight: 6,
                                                                                transform: isSubOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                                opacity: isSubOpen ? 1 : 0,
                                                                                transition: 'opacity 220ms cubic-bezier(.2,.8,.2,1), transform 220ms cubic-bezier(.2,.8,.2,1)',
                                                                                pointerEvents: 'none',
                                                                            }}
                                                                        />
                                                                        <span className={`menu-row-title ${getSubHeaderDepthClass(subHeader.title)}`} style={{ marginLeft: 6 }}>
                                                                            {t(subHeader.title)}
                                                                        </span>
                                                                    </Menu.Item>
                                                                </Accordion.Title>
                                                                <Transition visible={isSubOpen} animation='fade down' duration={220} unmountOnHide={false}>
                                                                    <Accordion.Content active={isSubOpen} style={{ marginTop: 0, paddingTop: 0 }}>
                                                                        <Menu vertical fluid text style={{ paddingLeft: '30px' }}>
                                                                            {routes.map((route, indexR) => {
                                                                                if (route.sub_group !== subHeader.title) return null
                                                                                const active = isActiveRoute(route.url)
                                                                                return (
                                                                                    <Link
                                                                                        key={`link-sub-${indexR}-${route.url}`}
                                                                                        to={route.url}
                                                                                        className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''}`}
                                                                                        onClick={(e) => handleTabOpen(e, route)}
                                                                                    >
                                                                                        <Menu.Item
                                                                                            className={styles.growOnHover}
                                                                                            style={{ margin: 0, display: 'flex', alignItems: 'center' }}
                                                                                            onClick={() => handleClickLink(route, header.module_group)}
                                                                                        >
                                                                                            {route.icon ? (
                                                                                                <Icon name={route.icon} size='small' style={{ marginRight: 8 }} />
                                                                                            ) : null}
                                                                                            <span
                                                                                                ref={active ? activeItemRef : null}
                                                                                                className={`menu-item-label ${getRouteDepthClassForSubHeader(subHeader.title)}`}
                                                                                                style={{ marginLeft: 8 }}
                                                                                            >
                                                                                                {t(route.name)}
                                                                                            </span>
                                                                                        </Menu.Item>
                                                                                    </Link>
                                                                                )
                                                                            })}
                                                                        </Menu>
                                                                    </Accordion.Content>
                                                                </Transition>
                                                            </Accordion>
                                                        )
                                                    })}

                                                    {/* Rutas sin subgrupo */}
                                                    <Menu vertical fluid text style={{ paddingLeft: '18px', width: '100%' }}>
                                                        {routes.map((route, indexR) => {
                                                            if (route.group !== header.module_group || route.sub_group) return null
                                                            const active = isActiveRoute(route.url)
                                                            return (
                                                                <Link
                                                                    key={`link-${indexR}-${route.url}`}
                                                                    to={route.url}
                                                                    className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''} ${isOpen ? 'depth-parent' : ''}`}
                                                                    onClick={(e) => handleTabOpen(e, route)}
                                                                >
                                                                    <Menu.Item
                                                                        className={styles.growOnHover}
                                                                        style={{ margin: 0, display: 'flex', alignItems: 'center' }}
                                                                        onClick={() => handleClickLink(route, header.module_group)}
                                                                    >
                                                                        {route.icon ? (
                                                                            <Icon name={route.icon} size='small' style={{ marginRight: 8 }} />
                                                                        ) : null}
                                                                        <span
                                                                            ref={active ? activeItemRef : null}
                                                                            className={`menu-item-label ${getRouteDepthClassForHeader(index)}`}
                                                                            style={{ marginLeft: 8 }}
                                                                        >
                                                                            {t(route.name)}
                                                                        </span>
                                                                    </Menu.Item>
                                                                </Link>
                                                            )
                                                        })}
                                                    </Menu>
                                                </Grid.Column>
                                            </Grid.Row>
                                        </Grid>
                                    </Accordion.Content>
                                </Transition>
                            </Accordion>
                        )
                    })}
                </div>
            </Transition>
        </div>
    )
}

const mapStateToProps = (state) => ({
    supplier: state.app.supplier,
    lang: state.app.lang, // Añadido lang
})

const mapDispatchToProps = (dispatch) => ({
    getSupplierModules: () => dispatch(supplierActions.getSupplierModules()),
    getWebSupplierPermissions: (supplierId) =>
        dispatch(supplierActions.getWebSupplierPermissions(supplierId)),
    actionClick: (data) => dispatch(supplierActions.actionClick(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(SupplierSidebarMenu)
